import { useState, useEffect, ReactNode } from 'react';
import { useMsal, useAccount } from '@azure/msal-react';
import { loginRequest, freshIdToken } from '../authConfig';
import { apiUrl } from '../config';
import { AuthContext, type User } from './auth-context';

export function AuthProvider({ children }: { children: ReactNode }) {
    const { instance, accounts } = useMsal();
    const account = useAccount(accounts[0] || null);
    const [user, setUser] = useState<User | null>(null);
    const [token, setToken] = useState<string | null>(null);
    // Compte MSAL pour lequel la connexion à l'API a abouti, réussie ou non. Le
    // chargement s'en déduit au lieu d'être un état posé dans l'effet : un compte
    // présent mais pas encore traité, c'est une connexion en cours.
    const [settledFor, setSettledFor] = useState<string | null>(null);
    const isLoading = !!account && settledFor !== account.homeAccountId;

    useEffect(() => {
        if (!account) return;
        // La réponse d'une connexion lancée pour un compte qui n'est plus le compte
        // courant (déconnexion, changement de compte entre-temps) est ignorée.
        let abandonnee = false;

        const appelerApi = async (): Promise<{ token: string; user: User }> => {
            // L'API établit l'identité à partir de l'id_token, qu'elle vérifie : c'est lui
            // qui est transmis, et non le jeton d'accès Graph, qu'elle ne peut pas vérifier.
            const idToken = await freshIdToken(instance, account);
            const backendResponse = await fetch(apiUrl('/api/auth/login'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    idToken,
                    // Ignorés par une API à jour, qui les lit dans le jeton ; conservés pour
                    // qu'une API pas encore mise à jour accepte toujours la connexion.
                    azureId: account.localAccountId,
                    email: account.username,
                    displayName: account.name,
                    firstName: account.name?.split(' ')[0],
                    lastName: account.name?.split(' ').slice(1).join(' '),
                }),
            });
            if (!backendResponse.ok) {
                throw new Error('Backend authentication failed');
            }
            const data = await backendResponse.json();
            return data.data;
        };

        const connecter = async () => {
            let resultat: { token: string; user: User } | null = null;
            try {
                resultat = await appelerApi();
            } catch (error) {
                console.error('Authentication error:', error);
                // Une seule tentative interactive. La version précédente se rappelait
                // elle-même après la fenêtre de connexion, et en rouvrait une nouvelle
                // indéfiniment tant que l'API refusait.
                try {
                    await instance.acquireTokenPopup(loginRequest);
                    resultat = await appelerApi();
                } catch (secondError) {
                    console.error('Authentication error after interactive login:', secondError);
                }
            }
            if (abandonnee) return;
            setToken(resultat?.token ?? null);
            setUser(resultat?.user ?? null);
            setSettledFor(account.homeAccountId);
        };

        void connecter();
        return () => {
            abandonnee = true;
        };
    }, [account, instance]);

    const login = async () => {
        try {
            await instance.loginPopup(loginRequest);
        } catch (error) {
            console.error('Login error:', error);
        }
    };

    const logout = () => {
        instance.logoutPopup();
        setUser(null);
        setToken(null);
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                token,
                isLoading,
                isAuthenticated: !!user && !!token,
                login,
                logout,
                isManager: user?.role === 'manager' || user?.role === 'admin',
                isAdmin: user?.role === 'admin',
                isBilling: user?.role === 'billing' || user?.role === 'admin',
                isCdp: user?.isCdp === true,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}
